/**
 * /fr/transcription-audio-en-texte — audio/video-to-text reframe bridge (FR).
 *
 * Targets transcription audio en texte (700) / transcription video en texte
 * gratuit (600) / transcrire mp3 en texte. Links to /fr/retranscription-entretien.
 */
import { MarketingShell } from './MarketingShell';

export function FrTranscriptionAudio() {
  return (
    <MarketingShell
      currentLocale="fr"
      hreflangPath="/transcription-audio-en-texte"
      hreflangLocales={['fr']}
      documentTitle="Transcription audio en texte : gratuit, rapide — et après ? · Draftlize"
      documentDescription="Transcrire un audio ou une vidéo en texte gratuitement, puis extraire ce qui compte vraiment : les décisions. Le guide + l'étape que tout le monde oublie."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Transcription</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Transcrire un audio en texte<br />
            <em>— et ne pas s'arrêter là.</em>
          </h1>
          <p className="lede">
            Tu déposes un fichier audio ou une vidéo et, en quelques secondes, tu obtiens une transcription. Les outils — Notta, tl;dv, Whisper — sont excellents pour ça. Mais une transcription est <em>matière première</em>, pas un livrable. Ce qui comptait vraiment — la <em>décision</em> prise et pourquoi — se perd dans des milliers de mots, finit dans un fichier que personne ne rouvre et devient caduc en semaines. Cette page couvre le comment — et l'étape que tout le monde oublie.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Transcrire un audio (ou une vidéo) gratuitement.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Récupère le fichier</strong> — MP3, WAV, ou une vidéo.</li>
            <li><strong>Transcris</strong> — un outil IA convertit la voix en texte, souvent avec un quota gratuit.</li>
            <li><strong>Relis</strong> — corrige les noms propres, puis exporte.</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Ça marche pour l'audio comme pour la vidéo, en français comme dans d'autres langues. Mais la vraie question arrive ensuite. Pour les entretiens, vois aussi <a href="/fr/retranscription-entretien" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>retranscrire un entretien</a>.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Matière première vs contexte vivant</span>
            <h2 className="serif">La transcription, c'est l'entrée. <em>Le contexte produit, la sortie.</em></h2>
          </div>
          <p>Ton outil de transcription et Draftlize font des métiers différents. L'un met en texte ; l'autre garde vivantes les décisions qui en sortent.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>Outil de transcription</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Sortie</td><td className="partial">Transcription + résumé</td><td className="ours yes">Cartes de décision reliées</td></tr>
            <tr><td>Une décision en renverse une autre</td><td className="no">Deux notes sans lien</td><td className="ours yes">Reliées ; l'ancienne passe en « stale »</td></tr>
            <tr><td>Trois semaines plus tard</td><td className="no">Enseveli dans un fichier</td><td className="ours yes">Adressable, à jour ou signalé</td></tr>
            <tr><td>Un agent IA peut s'en servir</td><td className="partial">Lit du texte brut</td><td className="ours yes">Lit + écrit via MCP</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Ton outil transcrit l'audio à la perfection. Il ne peut juste pas te dire, trois semaines après, quelles décisions à l'intérieur tiennent encore.
        </blockquote>
        <cite>Garde l'outil de transcription. Laisse Draftlize garder les décisions vivantes.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Questions fréquentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Quel outil gratuit pour transcrire un audio ?', a: 'Notta, tl;dv, Whisper — celui qui te convient. Draftlize ne transcrit pas : il intervient une fois le texte obtenu, pour en extraire les décisions et les garder à jour.' },
            { q: 'Quelle précision ?', a: 'La transcription IA est désormais fiable, y compris en français. Mais plus elle est précise, plus le mur de texte grossit — d\'où l\'étape d\'extraire les décisions.' },
            { q: 'Comment transcrire une vidéo (YouTube) ?', a: 'Passe l\'URL à un outil compatible ou exporte les sous-titres. Pour une réunion ou un entretien, ajoute l\'étape qui compte : conserver les décisions.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">La transcription est l'entrée. Le contexte produit, la sortie <em>— gratuit avec 5 $.</em></h2>
        </div>
        <div className="trust-strip">
          <span>5 $ offerts à l'inscription</span>
          <span>Payez seulement ce que vous utilisez</span>
          <span>Le solde n'expire jamais</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Garde ton outil de transcription. Dépose le résumé dans Draftlize : les décisions à l'intérieur deviennent des cartes vivantes — reliées, à jour et lisibles par ton agent IA.
          </p>
          <a className="btn-primary" href="/">Commencer gratuitement avec 5 $</a>
        </div>
      </section>
    </MarketingShell>
  );
}
